// src/services/flightDataService.js

// Common airline codes and names
const AIRLINES = [
    { code: "AA", name: "American Airlines" },
    { code: "AC", name: "Air Canada" },
    { code: "AF", name: "Air France" },
    { code: "AM", name: "Aeromexico" },
    { code: "AS", name: "Alaska Airlines" },
    { code: "AY", name: "Finnair" },
    { code: "AZ", name: "Alitalia" },
    { code: "B6", name: "JetBlue Airways" },
    { code: "BA", name: "British Airways" },
    { code: "CA", name: "Air China" },
    { code: "CI", name: "China Airlines" },
    { code: "CX", name: "Cathay Pacific" },
    { code: "DL", name: "Delta Air Lines" },
    { code: "EK", name: "Emirates" },
    { code: "ET", name: "Ethiopian Airlines" },
    { code: "EY", name: "Etihad Airways" },
    { code: "F9", name: "Frontier Airlines" },
    { code: "G3", name: "GOL Airlines" },
    { code: "HA", name: "Hawaiian Airlines" },
    { code: "IB", name: "Iberia" },
    { code: "JL", name: "Japan Airlines" },
    { code: "KE", name: "Korean Air" },
    { code: "KL", name: "KLM Royal Dutch Airlines" },
    { code: "LA", name: "LATAM Airlines" },
    { code: "LH", name: "Lufthansa" },
    { code: "LX", name: "Swiss International Air Lines" },
    { code: "MH", name: "Malaysia Airlines" },
    { code: "MU", name: "China Eastern Airlines" },
    { code: "NH", name: "ANA (All Nippon Airways)" },
    { code: "NK", name: "Spirit Airlines" },
    { code: "NZ", name: "Air New Zealand" },
    { code: "OS", name: "Austrian Airlines" },
    { code: "OZ", name: "Asiana Airlines" },
    { code: "PR", name: "Philippine Airlines" },
    { code: "QF", name: "Qantas" },
    { code: "QR", name: "Qatar Airways" },
    { code: "SA", name: "South African Airways" },
    { code: "SK", name: "SAS Scandinavian Airlines" },
    { code: "SQ", name: "Singapore Airlines" },
    { code: "SU", name: "Aeroflot" },
    { code: "SV", name: "Saudia" },
    { code: "TG", name: "Thai Airways" },
    { code: "TK", name: "Turkish Airlines" },
    { code: "TP", name: "TAP Air Portugal" },
    { code: "UA", name: "United Airlines" },
    { code: "VS", name: "Virgin Atlantic" },
    { code: "VX", name: "Virgin America" },
    { code: "WN", name: "Southwest Airlines" },
    { code: "WS", name: "WestJet" },
    { code: "ZH", name: "Shenzhen Airlines" }
];

// Common IATA airport codes
const AIRPORTS = {
    'JFK': { name: 'John F. Kennedy International', city: 'New York', timezone: 'America/New_York' },
    'LAX': { name: 'Los Angeles International', city: 'Los Angeles', timezone: 'America/Los_Angeles' },
    'LHR': { name: 'London Heathrow', city: 'London', timezone: 'Europe/London' },
    // Add more as needed
};

// Firebase imports using correct path
import { db, doc, collection, getDoc, setDoc, updateDoc, arrayUnion, arrayRemove, query, where, getDocs } from '@trip-planner-monorepo/shared/firebase/firebaseInit';

// Simple in-memory mock database for development
const mockDb = {
  recentAirlines: []
};

export const flightDataService = {
    /**
     * Parse flight number to extract airline code and number
     * @param {string} flightNumber - Full flight number (e.g., "AA123")
     * @returns {Object} Parsed flight information
     */
    parseFlightNumber(flightNumber) {
        if (!flightNumber) return null;

        // Most flight numbers follow the pattern of 2-3 letters followed by 1-4 numbers
        const match = flightNumber.trim().match(/^([A-Z]{2,3})(\d{1,4})$/i);
        if (!match) return null;

        const [_, airlineCode, number] = match;
        return {
            airlineCode: airlineCode.toUpperCase(),
            flightNumber: number,
            fullNumber: `${airlineCode.toUpperCase()}${number}`,
            airline: AIRLINES.find(a => a.code === airlineCode.toUpperCase()) || null
        };
    },

    /**
     * Validate flight number format
     * @param {string} flightNumber - Flight number to validate
     * @returns {Object} Validation result
     */
    validateFlightNumber(flightNumber) {
        if (!flightNumber) {
            return { isValid: false, error: 'Flight number is required' };
        }

        const parsed = this.parseFlightNumber(flightNumber);
        if (!parsed) {
            return {
                isValid: false,
                error: 'Invalid flight number format. Should be airline code followed by numbers (e.g., AA123)'
            };
        }

        return { isValid: true, parsed };
    },

    /**
     * Validate IATA airport code
     * @param {string} code - Airport code to validate
     * @returns {Object} Validation result
     */
    validateAirportCode(code) {
        if (!code) {
            return { isValid: false, error: 'Airport code is required' };
        }

        const normalizedCode = code.trim().toUpperCase();
        if (!/^[A-Z]{3}$/.test(normalizedCode)) {
            return {
                isValid: false,
                error: 'Invalid airport code. Should be 3 letters (IATA code)'
            };
        }

        const airportInfo = AIRPORTS[normalizedCode];
        return {
            isValid: true,
            code: normalizedCode,
            info: airportInfo || null
        };
    },

    /**
     * Get list of known airlines with recently used ones at the top
     * @returns {Array} List of airline objects
     */
    getAirlinesList(userId, tripId) {
        // Get standard airlines list
        const standardAirlines = AIRLINES.map(({ code, name }) => ({
            code,
            name,
            standard: true,
            label: `${code} - ${name}`
        }));
        
        // If we don't have user context, return standard list only
        if (!userId) {
            return standardAirlines;
        }
        
        try {
            // Get recently used airlines from localStorage only (fully synchronous)
            const storageKey = `recentAirlines_${userId}${tripId ? '_' + tripId : ''}`;
            const storedData = localStorage.getItem(storageKey);
            const recentlyUsed = storedData ? JSON.parse(storedData) : [];
            
            // Mark them as recent for UI distinction
            const recentAirlines = recentlyUsed.map(airline => ({
                ...airline,
                recent: true,
                label: `${airline.code} - ${airline.name}`
            }));
            
            // Filter out airlines from standard list that are in recent list
            const filteredStandard = standardAirlines.filter(
                stdAirline => !recentAirlines.some(
                    recent => recent.code === stdAirline.code
                )
            );
            
            // Combine lists with recently used first
            return [...recentAirlines, ...filteredStandard];
        } catch (error) {
            console.error('Error getting airlines list:', error);
            // Fallback to standard list on error
            return standardAirlines;
        }
    },

    /**
     * Get list of known airports
     * @returns {Array} List of airport objects
     */
    getAirportsList() {
        return Object.entries(AIRPORTS).map(([code, info]) => ({
            code,
            name: info.name,
            city: info.city,
            timezone: info.timezone,
            label: `${code} - ${info.city} (${info.name})`
        }));
    },

    /**
     * Validate flight times considering timezones
     * @param {Object} departure - Departure date/time info
     * @param {Object} arrival - Arrival date/time info
     * @returns {Object} Validation result
     */
    validateFlightTimes(departure, arrival) {
        if (!departure || !arrival) {
            return { isValid: false, error: 'Both departure and arrival times are required' };
        }

        try {
            const departureTime = new Date(departure.timestamp);
            const arrivalTime = new Date(arrival.timestamp);

            if (isNaN(departureTime.getTime()) || isNaN(arrivalTime.getTime())) {
                return { isValid: false, error: 'Invalid date/time format' };
            }

            if (arrivalTime <= departureTime) {
                return { isValid: false, error: 'Arrival time must be after departure time' };
            }

            const duration = (arrivalTime - departureTime) / (1000 * 60 * 60); // Duration in hours
            if (duration > 20) {
                return {
                    isValid: true,
                    warning: 'Flight duration exceeds 20 hours. Please verify times are correct.'
                };
            }

            return { isValid: true };
        } catch (error) {
            return { isValid: false, error: 'Error validating flight times' };
        }
    },

    /**
     * Generate a display title for a flight
     * @param {Object} flightData - Flight information
     * @returns {string} Formatted display title
     */
    formatFlightTitle(flightData) {
        if (!flightData) return 'New Flight';
        
        // Debug logging to inspect the incoming data
        console.log('Formatting flight title with data:', JSON.stringify(flightData, null, 2));
        
        let title = '';
        
        // Part 1: Airline and Flight Number - Format as "Qantas QF123"
        let airlineCode = '';
        let airlineName = '';
        
        if (flightData.airline) {
            // Check for different formats of airline data
            if (typeof flightData.airline === 'object') {
                // Handle object format (from dropdown selections)
                if (flightData.airline.isCustom) {
                    // Use custom airline name directly
                    airlineName = flightData.airline.name || flightData.airline.label || '';
                } else {
                    // For standard airlines, we may have the code and name separated
                    airlineCode = flightData.airline.code || flightData.airline.value || '';
                    airlineName = flightData.airline.name || flightData.airline.label || '';
                }
            } else if (typeof flightData.airline === 'string') {
                // Find airline in AIRLINES list if possible
                const matchingAirline = AIRLINES.find(a => 
                    a.code === flightData.airline || 
                    a.name === flightData.airline ||
                    flightData.airline.includes(a.code) ||
                    flightData.airline.includes(a.name)
                );
                
                if (matchingAirline) {
                    airlineCode = matchingAirline.code;
                    airlineName = matchingAirline.name;
                } else if (flightData.airline.includes(' - ')) {
                    // Parse from "CODE - Name" format
                    const parts = flightData.airline.split(' - ');
                    airlineCode = parts[0].trim();
                    airlineName = parts[1].trim();
                } else {
                    // Just use it as is
                    airlineName = flightData.airline;
                }
            }
            
            // Use only the airline name in the title
            title = airlineName;
            
            // Add flight number if available, but don't duplicate airline code
            if (flightData.flightNumber) {
                title += ' ' + flightData.flightNumber;
            }
        } else if (flightData.flightNumber) {
            title = flightData.flightNumber;
        }
        
        // Part 2: Route - Format as "City to City"
        const hasRoute = flightData.departureCity || flightData.arrivalCity || 
                      flightData.departureAirport || flightData.arrivalAirport;
        
        if (hasRoute) {
            if (title) title += ', ';
            
            // Helper function to extract text from city/airport object or string
            const extractNameFromObject = (obj) => {
                if (!obj) return '';
                
                // If it's a string, return it directly
                if (typeof obj === 'string') return obj;
                
                // If it's an object, try to extract a meaningful name
                if (typeof obj === 'object') {
                    // Try all possible property names that might contain the display name
                    return obj.name || obj.label || obj.value || obj.code || 
                           (obj.toString && obj.toString() !== '[object Object]' ? obj.toString() : 'Unknown');
                }
                
                // Fallback for unexpected types
                return String(obj);
            };
            
            // Extract departure city or airport name
            let departure = 'Departure';
            if (flightData.departureCity) {
                departure = extractNameFromObject(flightData.departureCity);
            } else if (flightData.departureAirport) {
                departure = extractNameFromObject(flightData.departureAirport);
            }
            
            // Extract arrival city or airport name
            let arrival = 'Arrival';
            if (flightData.arrivalCity) {
                arrival = extractNameFromObject(flightData.arrivalCity);
            } else if (flightData.arrivalAirport) {
                arrival = extractNameFromObject(flightData.arrivalAirport);
            }
            
            title += `${departure} to ${arrival}`;
        }
        
        // Part 3: Add departure date and local times in format "15 Jun 2025, 10:30 → 14:45"
        let dateTimeInfo = '';
        
        // Get departure date and time
        if (flightData.departureTime) {
            const departureDate = new Date(flightData.departureTime);
            
            if (departureDate && !isNaN(departureDate.getTime())) {
                // Format date as "15 Jun 2025"
                const day = departureDate.getDate();
                const month = departureDate.toLocaleString('en-US', { month: 'short' });
                const year = departureDate.getFullYear();
                
                dateTimeInfo = `${day} ${month} ${year}`;
                
                // Add local departure time
                const departureTime = departureDate.toLocaleTimeString('en-US', { 
                    hour: '2-digit', 
                    minute: '2-digit', 
                    hour12: false 
                });
                dateTimeInfo += `, ${departureTime}`;
                
                // Add arrival time if available
                if (flightData.arrivalTime) {
                    const arrivalDate = new Date(flightData.arrivalTime);
                    if (arrivalDate && !isNaN(arrivalDate.getTime())) {
                        const arrivalTime = arrivalDate.toLocaleTimeString('en-US', { 
                            hour: '2-digit', 
                            minute: '2-digit', 
                            hour12: false 
                        });
                        dateTimeInfo += ` → ${arrivalTime}`;
                    }
                }
            }
        } else if (flightData.startDate) {
            // If no specific departure time, use the start date
            const departureDate = new Date(flightData.startDate);
            
            if (departureDate && !isNaN(departureDate.getTime())) {
                // Format date as "15 Jun 2025"
                const day = departureDate.getDate();
                const month = departureDate.toLocaleString('en-US', { month: 'short' });
                const year = departureDate.getFullYear();
                
                dateTimeInfo = `${day} ${month} ${year}`;
            }
        }
        
        // Add date and time information to title
        if (dateTimeInfo) {
            if (title) title += ' (';
            title += dateTimeInfo;
            if (title.endsWith('(')) title += ')';
            else if (!title.endsWith(')')) title += ')';
        }
        
        console.log('Generated flight title:', title);
        return title || 'New Flight';
    },

    /**
     * Calculate completeness score for flight data
     * @param {Object} flightData - Flight information
     * @returns {Object} Completeness information
     */
    calculateCompleteness(flightData) {
        const requiredFields = ['flightNumber', 'departureAirport', 'arrivalAirport', 'departureTime', 'arrivalTime'];
        const optionalFields = ['airline', 'bookingReference', 'seatNumber', 'notes'];
        
        let completed = 0;
        let total = requiredFields.length;

        // Check required fields
        for (const field of requiredFields) {
            if (flightData[field]) completed++;
        }

        // Add partial credit for optional fields
        let optionalCompleted = 0;
        for (const field of optionalFields) {
            if (flightData[field]) optionalCompleted++;
        }

        // Optional fields contribute less to the total score
        completed += (optionalCompleted / optionalFields.length) * 0.5;
        total += 0.5;

        const score = (completed / total) * 100;
        
        return {
            score: Math.round(score),
            isComplete: score === 100,
            missingRequired: requiredFields.filter(field => !flightData[field])
        };
    },

    /**
     * Track recently used airline for a user
     * Stores in localStorage for simplicity
     */
    trackRecentlyUsedAirline(userId, tripId, airlineData) {
        if (!userId || !airlineData || !airlineData.code) return;

        try {
            const storageKey = `recentAirlines_${userId}${tripId ? '_' + tripId : ''}`;
            
            // Get existing recent airlines
            const storedData = localStorage.getItem(storageKey);
            let recentAirlines = storedData ? JSON.parse(storedData) : [];
            
            // Add timestamp
            const airlineWithTimestamp = {
                ...airlineData,
                lastUsed: new Date().toISOString()
            };
            
            // Remove if exists (to avoid duplicates)
            recentAirlines = recentAirlines.filter(airline => airline.code !== airlineData.code);
            
            // Add to beginning of array
            recentAirlines.unshift(airlineWithTimestamp);
            
            // Keep only top 5
            recentAirlines = recentAirlines.slice(0, 5);
            
            // Save back to localStorage
            localStorage.setItem(storageKey, JSON.stringify(recentAirlines));
            
            return recentAirlines;
        } catch (error) {
            console.error('Error tracking recently used airline:', error);
            return [];
        }
    },

    /**
     * Get recently used airlines for a user from localStorage
     * @returns {Array} List of recently used airlines
     */
    getRecentlyUsedAirlines(userId, tripId) {
        try {
            const storageKey = `recentAirlines_${userId}${tripId ? '_' + tripId : ''}`;
            const storedData = localStorage.getItem(storageKey);
            const recentAirlines = storedData ? JSON.parse(storedData) : [];
            
            // Sort by last used date (newest first)
            return recentAirlines.sort((a, b) => new Date(b.lastUsed) - new Date(a.lastUsed));
        } catch (error) {
            console.error('Error getting recently used airlines:', error);
            return [];
        }
    }
};